// src/components/NotificationsDropdown.js

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationAsRead,
} from "../actions/notificationActions";
import { LazyLoading } from "./ui/LazyLoading";

import {
  FaBell,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
} from "react-icons/fa";

const MAX_DISPLAY = 4; // Maximum number of notifications to display

const NotificationsDropdown = () => {
  const dispatch = useDispatch();
  const {
    items: notifications,
    loading,
    error,
    hasFetched,
  } = useSelector((state) => state.notifications);

  // Helper function to truncate messages
  const truncateMessage = (text, maxLength = 40) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "…";
  };

  useEffect(() => {
    // Only fetch if we haven't fetched them yet
    if (!hasFetched) {
      dispatch(fetchNotifications());
    }
  }, [dispatch, hasFetched]);

  // Handlers
  const handleMarkAsRead = (id) => {
    dispatch(markNotificationAsRead(id));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationAsRead());
  };

  // Filter UNREAD notifications
  const unreadNotifications = notifications.filter((notif) => !notif.is_read);

  // Determine displayed and hidden notifications
  const displayedNotifications = unreadNotifications.slice(0, MAX_DISPLAY);
  const hiddenNotificationsCount =
    unreadNotifications.length > MAX_DISPLAY
      ? unreadNotifications.length - MAX_DISPLAY
      : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <LazyLoading />
      </div>
    );
  }

  if (error) {
    return (
      <div
        dir="rtl"
        className="text-[0.8rem] text-red-300 bg-red-950/40 border border-red-700/50 rounded-2xl px-3 py-2 text-right"
      >
        خطا در دریافت اعلان‌ها: {error}
      </div>
    );
  }

  // Helper for icon + color per type
  const getTypeConfig = (type) => {
    switch (type) {
      case "success":
        return {
          icon: <FaCheckCircle className="text-emerald-400 text-xs" />,
          ring: "border-emerald-500/60 bg-emerald-500/10",
          dot: "bg-emerald-400",
        };
      case "warning":
        return {
          icon: <FaExclamationTriangle className="text-amber-400 text-xs" />,
          ring: "border-amber-500/60 bg-amber-500/10",
          dot: "bg-amber-400",
        };
      case "error":
        return {
          icon: <FaTimesCircle className="text-rose-400 text-xs" />,
          ring: "border-rose-500/60 bg-rose-500/10",
          dot: "bg-rose-400",
        };
      case "info":
      default:
        return {
          icon: <FaInfoCircle className="text-sky-400 text-xs" />,
          ring: "border-sky-500/60 bg-sky-500/10",
          dot: "bg-sky-400",
        };
    }
  };

  return (
    <div
      dir="rtl"
      className="w-full text-right text-xs sm:text-[0.8rem] text-slate-100"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex flex-row-reverse items-center gap-2">
          <span className="inline-flex w-7 h-7 rounded-xl bg-slate-800/90 border border-slate-600/70 items-center justify-center">
            <FaBell className="text-[0.8rem] text-sky-400" />
          </span>
          <div className="flex flex-col items-end">
            <span className="font-medium text-[0.8rem]">اعلان‌ها</span>
            <span className="text-[0.7rem] text-slate-400">
              {unreadNotifications.length === 0
                ? "همه اعلان‌ها خوانده شده‌اند"
                : `${unreadNotifications.length} اعلان خوانده‌نشده`}
            </span>
          </div>
        </div>

        {unreadNotifications.length > 0 && (
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            className="px-2 py-1 rounded-full text-[0.7rem]
                       bg-slate-800/80 border border-slate-600/60
                       hover:bg-slate-700/80 text-slate-100
                       transition-colors"
          >
            خواندن همه
          </button>
        )}
      </div>

      {/* Body */}
      {unreadNotifications.length === 0 ? (
        <div className="rounded-2xl border border-slate-700/70 bg-slate-900/70 px-3 py-3 text-[0.8rem] text-slate-300">
          هیچ اعلان خوانده‌نشده‌ای وجود ندارد.
        </div>
      ) : (
        <>
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {displayedNotifications.map((notif) => {
              const cfg = getTypeConfig(notif.type);
              return (
                <div
                  key={notif.id}
                  className={`flex flex-row-reverse items-start gap-2 rounded-2xl px-3 py-2
                              bg-slate-900/80 border border-slate-700/80`}
                >
                  {/* Icon + dot */}
                  <div className="flex flex-col items-center justify-start pt-0.5">
                    <div
                      className={`w-7 h-7 rounded-xl flex items-center justify-center border ${cfg.ring}`}
                    >
                      {cfg.icon}
                    </div>
                    {!notif.is_read && (
                      <span
                        className={`mt-1 w-1.5 h-1.5 rounded-full ${cfg.dot}`}
                      ></span>
                    )}
                  </div>

                  {/* Message + action */}
                  <div className="flex-1 flex flex-col items-end gap-1">
                    <p className="text-[0.8rem] text-slate-100 leading-relaxed">
                      {truncateMessage(notif.message, 40)}
                    </p>
                    <div className="flex flex-row-reverse items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleMarkAsRead(notif.id)}
                        className="px-2 py-1 rounded-full text-[0.7rem]
                                   bg-slate-800/80 border border-slate-600/70
                                   hover:bg-slate-700/80 text-slate-100
                                   transition-colors"
                      >
                        خوانده شد
                      </button>
                      {/* Optional: show type label */}
                      <span className="text-[0.65rem] text-slate-400">
                        {notif.type === "success"
                          ? "موفق"
                          : notif.type === "warning"
                          ? "هشدار"
                          : notif.type === "error"
                          ? "خطا"
                          : "اطلاع"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {hiddenNotificationsCount > 0 && (
              <div className="mt-1 rounded-2xl bg-slate-900/80 border border-dashed border-slate-700/70 px-3 py-2 text-[0.75rem] text-slate-300">
                و {hiddenNotificationsCount} اعلان خوانده‌نشده دیگر
              </div>
            )}
          </div>
        </>
      )}

      {/* Footer link */}
      <div className="mt-2 pt-2 border-t border-slate-800/80">
        <Link
          to="/notifications/all"
          className="block text-[0.75rem] text-sky-400 hover:text-sky-300 text-center"
        >
          مشاهده همه اعلان‌ها
        </Link>
      </div>
    </div>
  );
};

export default NotificationsDropdown;
